import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";
import { servicePages, serviceCommon, serviceLinks } from "@/i18n/seoPages";

const SITE = "https://reflectumpellicule.com";

export default function ServicePage({ pageKey }) {
  const { lang } = useLanguage();
  const p = servicePages[lang][pageKey];
  const common = serviceCommon[lang];

  useEffect(() => {
    if (!p) return undefined;
    document.title = p.metaTitle;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", p.metaDesc);
    let canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", `${SITE}${p.path}`);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOg = ogUrl?.getAttribute("content");
    if (ogUrl) ogUrl.setAttribute("content", `${SITE}${p.path}`);
    return () => {
      if (canonical && prev) canonical.setAttribute("href", prev);
      if (ogUrl && prevOg) ogUrl.setAttribute("content", prevOg);
    };
  }, [p, lang]);

  if (!p) return null;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: p.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="animate-fade-up">
      <JsonLd id={`faq-${pageKey}`} data={faqLd} />

      {/* HERO */}
      <section data-testid="service-hero" className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{p.eyebrow}</div>
            <h1 data-testid="service-title" className="font-heading text-[2.5rem] sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4">
              {p.h1}
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">{p.intro}</p>
            <Link to="/contact" data-testid="service-cta" className="block sm:inline-block">
              <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
                {common.ctaButton}
                <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-slate-500">{common.areasLine}</p>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.06)]">
            <img
              src={p.images[0].src}
              alt={p.images[0].alt}
              width={p.images[0].w}
              height={p.images[0].h}
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-14">
          {p.sections.map((s, i) => (
            <div key={s.title} data-testid={`service-section-${i}`}>
              <h2 className="font-heading text-2xl lg:text-4xl font-normal tracking-tight leading-[1.2] text-slate-900">
                {s.title}
              </h2>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTOS */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-12">
            {common.photosEyebrow}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {p.images.map((img, i) => (
              <figure key={img.src} data-testid={`service-photo-${i}`} className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
                <img
                  src={img.src}
                  alt={img.alt}
                  width={img.w}
                  height={img.h}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="service-faq" className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-12">
            {common.faqEyebrow}
          </div>
          <Accordion type="single" collapsible className="w-full">
            {p.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger
                  data-testid={`service-faq-trigger-${i}`}
                  className="text-left font-heading text-lg font-medium text-slate-900 hover:text-blue-600"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`service-faq-content-${i}`} className="text-slate-600 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* EXPLORE + CTA */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400">{common.ctaEyebrow}</div>
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.2] mt-4">{common.ctaTitle}</h2>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">{common.ctaText}</p>
          <Link to="/contact" data-testid="service-cta-bottom" className="block sm:inline-block">
            <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
              {common.ctaButton}
              <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
            </Button>
          </Link>

          <div className="mt-16 pt-10 border-t border-white/10">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400 mb-6">{common.exploreTitle}</div>
            <div className="flex flex-wrap justify-center gap-3">
              {serviceLinks[lang]
                .filter((l) => l.to !== p.path)
                .map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`service-link-${l.to.slice(1)}`}
                    className="px-4 py-2 rounded-full border border-white/15 bg-white/5 text-sm text-slate-200 hover:bg-white/10 transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
